import * as log from 'loglevel'
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  FormControl,
  IconButton,
  InputAdornment,
  Input,
  InputLabel,
  Paper,
  Typography
} from '@mui/material'
import ROUTES from '../../routesConfig'
import AssignmentIcon from '@mui/icons-material/Assignment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import anearApi from '../Api/AnearApi'

import UserAuthContainer from '../Containers/UserAuthContainer'
import UIStateContainer from '../Containers/UIStateContainer'
import useNavigation from '../Utils/navigationUtils'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import GoogleOauthButton from '../UI/GoogleOauthButton'


const SignupPage = () => {

  const userAuthContainer = UserAuthContainer.useContainer()
  const uiStateContainer = UIStateContainer.useContainer()
  const location = useLocation()
  const {
    getNavigateComponent,
    navigateToLogin,
    navigateToGuestSignup
  } = useNavigation()
  let { from } = location.state || { from: { pathname: ROUTES.HOME } }
  const isGuestRecoveryFlow = Boolean(location.state?.recoveryGuestIdentity)
  const fromPath = from?.pathname || ''
  const isEventRecoveryFlow = isGuestRecoveryFlow && (fromPath.startsWith('/e/') || fromPath.startsWith('/p/'))
  const signupTitle = isEventRecoveryFlow
    ? 'Register to Rejoin Event'
    : (isGuestRecoveryFlow ? 'Register Your Account' : 'Anear Signup')
  const signupCtaLabel = isEventRecoveryFlow
    ? 'Register & Rejoin Event'
    : (isGuestRecoveryFlow ? 'Register Account' : 'Signup')

  const initName = () => {
    let nm = location.state?.recoveryGuestIdentity?.name || userAuthContainer.name() || ""
    if (nm.match(/^[Gg]uest/)) {
      nm = ""
    }
    return nm
  }

  const [ name, setName ] = useState(initName())
  const [ email, setEmail ] = useState('')
  const [ password, setPassword ] = useState('')
  const [ disableSubmitButton, setDisableSubmitButton ] = useState(false)
  const [ showPassword, setShowPassword ] = useState(false)
  const [ error, setError ] = useState(null)
  const [ redirectToReferrer, setRedirectToReferrer ] = useState(false)

  const clearLocalState = () => {
    setName(userAuthContainer.name()||"")
    setEmail("")
    setPassword("")
    setDisableSubmitButton(false)
    setShowPassword(false)
    setError(null)
  }

  const signupSuccess = async (user) => {
    uiStateContainer.progressComplete()
    clearLocalState()
    await userAuthContainer.setUser(user)
    setRedirectToReferrer(true)
  }

  const signupFailure = (error) => {
    // flash a message in the signup box and outline red
    uiStateContainer.progressComplete()
    log.error(error)
    setDisableSubmitButton(false)
    setError(error)
  }

  const handleSubmit = async (e) => {
    uiStateContainer.startProgress()
    e.preventDefault()

    try {
      setDisableSubmitButton(true)
      const user = await anearApi.register(
        name,
        email,
        password
      )
      await signupSuccess(user)
    } catch(error) {
      signupFailure(error)
    }
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword)

  const guestButtonHandler = (e) => {
    e.preventDefault()
      clearLocalState()
    navigateToGuestSignup(
      from,
      { source: 'signup' }
    )
  }

  const loginButtonHandler = e => {
    e.preventDefault()

    navigateToLogin(from)
  }

  const oauthReturnTarget = `${from?.pathname || ROUTES.HOME}${from?.search || ''}${from?.hash || ''}`

  if (redirectToReferrer || userAuthContainer.isRegisteredAuthed()) return getNavigateComponent(from)

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper sx={{
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 2,
        px: 3,
        pb: 3,
        bgcolor: '#0B0F19',
        color: '#E6E9EF',
        borderRadius: 3,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: CONTENT_COLUMN_MAX_WIDTH
      }}>
        <Avatar sx={{
          margin: 1,
          backgroundColor: 'rgba(124, 58, 237, 0.3)',
          color: '#A78BFA'
        }}>
          <AssignmentIcon />
        </Avatar>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            color: '#E6E9EF',
            fontWeight: 800,
            letterSpacing: 0.2
          }}
        >
          {signupTitle}
        </Typography>
        {isGuestRecoveryFlow && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: 'rgba(148,163,184,0.95)',
              textAlign: 'center',
              maxWidth: '31rem'
            }}
          >
            {isEventRecoveryFlow
              ? 'One quick step and we will take you right back to your event.'
              : 'Finish registration to keep your identity and continue where you left off.'}
          </Typography>
        )}
        <Box component="form"
          sx={{
            width: '100%',
            mt: 1
          }}
          autoComplete="off">
          <FormControl
            required
            fullWidth
            error={error !== null}
            sx={{
              mb: 2,
              '& label + .MuiInputBase-root': {
                marginTop: 0
              }
            }}
            variant="standard"
          >
            <InputLabel
              htmlFor="name"
              shrink={true}
              sx={{
                color: '#06B6D4',
                fontWeight: 700,
                fontSize: '0.875rem',
                transform: 'none',
                position: 'static',
                mb: 0.25
              }}
            >
              User Name
            </InputLabel>
            <Input
              id="name"
              name="name"
              required
              autoFocus
              value={name}
              sx={{
                color: '#E6E9EF',
                marginTop: '0 !important',
                '&::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.3)'
                },
                '&:hover:not(.Mui-disabled)::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.5)'
                },
                '&::after': {
                  borderBottom: '2px solid #7C3AED'
                }
              }}
              onChange={e => setName(e.target.value)}/>
          </FormControl>
          <FormControl
            required
            fullWidth
            error={error !== null}
            sx={{
              mb: 2,
              '& label + .MuiInputBase-root': {
                marginTop: 0
              }
            }}
            variant="standard"
          >
            <InputLabel
              htmlFor="email"
              shrink={true}
              sx={{
                color: '#06B6D4',
                fontWeight: 700,
                fontSize: '0.875rem',
                transform: 'none',
                position: 'static',
                mb: 0.25
              }}
            >
              Email Address
            </InputLabel>
            <Input
              id="email"
              name="email"
              required
              sx={{
                color: '#E6E9EF',
                marginTop: '0 !important',
                '&::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.3)'
                },
                '&:hover:not(.Mui-disabled)::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.5)'
                },
                '&::after': {
                  borderBottom: '2px solid #7C3AED'
                }
              }}
              onChange={e => setEmail(e.target.value)}/>
          </FormControl>
          <FormControl
            required
            fullWidth
            error={error !== null}
            sx={{
              mb: 2,
              '& label + .MuiInputBase-root': {
                marginTop: 0
              }
            }}
            variant="standard"
          >
            <InputLabel
              htmlFor="password"
              shrink={true}
              sx={{
                color: '#06B6D4',
                fontWeight: 700,
                fontSize: '0.875rem',
                transform: 'none',
                position: 'static',
                mb: 0.25
              }}
            >
              Password
            </InputLabel>
            <Input
              name="password"
              type={showPassword ? "text" : "password"}
              id="password"
              required
              sx={{
                color: '#E6E9EF',
                marginTop: '0 !important',
                '&::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.3)'
                },
                '&:hover:not(.Mui-disabled)::before': {
                  borderBottom: '1px solid rgba(230,233,239,0.5)'
                },
                '&::after': {
                  borderBottom: '2px solid #7C3AED'
                }
              }}
              onChange={e => setPassword(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={handleClickShowPassword}
                    sx={{ color: 'rgba(230,233,239,0.7)' }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>

          {/* Display error message in a box below the form fields */}
          {error && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#EF4444',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                {error.message || 'An error occurred. Please try again.'}
              </Typography>
            </Box>
          )}

          <Button
            sx={{
              mt: 3,
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 999,
              px: 3,
              py: 1.5,
              fontSize: '1.1rem',
              background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
              boxShadow: '0 0 32px rgba(124,58,237,0.45)',
              '&:hover': {
                background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                boxShadow: '0 0 40px rgba(124,58,237,0.55)'
              },
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(230,233,239,0.5)'
              }
            }}
            type="submit"
            fullWidth
            variant="contained"
            disabled={disableSubmitButton}
            onClick={e => handleSubmit(e)}
          >
            {signupCtaLabel}
          </Button>
        </Box>
        {!isGuestRecoveryFlow && (
          <>
            <GoogleOauthButton
              label="Sign up with Google"
              returnTarget={oauthReturnTarget}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: 'rgba(230,233,239,0.62)',
                textAlign: 'center'
              }}
            >
              Prefer Google? Create your Anear account with one tap.
            </Typography>
          </>
        )}
        <Button
          sx={{
            mt: 2,
            color: '#A78BFA',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            '&:hover': {
              bgcolor: 'rgba(167,139,250,0.1)'
            }
          }}
          size="small"
          onClick={e => loginButtonHandler(e)}
        >
          Login
        </Button>
        { !userAuthContainer.isAuthed() && (
            <Button
              sx={{
                mt: 2,
                color: '#A78BFA',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(167,139,250,0.1)'
                }
              }}
              size="small"
              onClick={e => guestButtonHandler(e)}
            >
              Continue As Guest
            </Button>
          )
        }
      </Paper>
    </Box>
  )
}

export default SignupPage
